import { Dialog, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TextInput } from '@common/components/inputs';
import { UserPreferenceName } from '@modules/userPreferences';
import { useUserPreference } from '@modules/userPreferences/hooks';
import { Theme } from '@modules/common/types/general';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { FILENAME_FORBIDDEN_CHARACTERS } from '@/modules/common/helpers/file';

import Label from '../common/components/Label';
import { useNavigation } from '../common/hooks';
import { logger } from '@common/logging/logger';

type Props = {
  isOpen: boolean;
  onCloseClick: () => void;
};

export function FloorplanCreator({ isOpen, onCloseClick }: Props) {
  const { projectId } = useParams();
  const [textInput, setTextInput] = useState('');
  const { t } = useTranslation(['interface', 'common']);
  const buttonRef = useRef(null);
  const { goToBuilder } = useNavigation();
  const { showSnackbar } = useSnackbarStore();
  const theme = useUserPreference(UserPreferenceName.GENERAL_THEME);

  const onCreateClick = useCallback(async () => {
    try {
      if (!projectId) {
        logger.error('projectId was undefined.');
        return;
      }

      const alternative = await ProjectServiceConnector.getInstance().createLayoutAlternative({
        projectId,
        alternativeName: textInput,
      });

      goToBuilder(alternative.alternativeId, alternative.lastRevisionId);

      AppServiceFactory.getInstance().createAppService().goToModule('canvas_setup');
    } catch (e) {
      showSnackbar(t('errors:home.create_floorplan'));
    }
  }, [t, showSnackbar, projectId, textInput, goToBuilder]);

  const handleTextInputChange = useCallback((value) => {
    setTextInput(value);
  }, []);

  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isOpen) setTextInput('');
  }, [isOpen]);

  return (
    <Dialog onClose={onCloseClick} open={isOpen} className={theme === Theme.DARK ? 'dark' : ''}>
      <Stack gap={2} p={2}>
        <Typography variant='h6' align='center'>
          {t('interface:home.create_new_floorplan', 'Create new floorplan')}
        </Typography>
        <Label label={t('interface:home.name', 'Name')}>
          <TextInput
            forbiddenCharacters={FILENAME_FORBIDDEN_CHARACTERS}
            value={textInput}
            onChange={handleTextInputChange}
          />
        </Label>
        <Stack gap={1}>
          <Button
            ref={buttonRef}
            variant='secondary'
            onClick={onCreateClick}
            disabled={textInput.length < 1}
          >
            {t('interface:home.create_button', 'Duplicate Floorplan')}
          </Button>
          <Button onClick={onCloseClick}>{t('common:cancel', 'Cancel')}</Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
